import React from "react";
import { Button } from "reactstrap";
import { resolveMediaUrl } from "./utils";

const GroupDetailsDialog = ({ isOpen, onClose, currentChat, showAllMembers, setShowAllMembers }) => {
  if (!isOpen || !currentChat || !currentChat._id) return null;

  const allMembers = [];

  if (Array.isArray(currentChat.superAdmins)) {
    currentChat.superAdmins.forEach(admin => {
      if (admin?._id) {
        allMembers.push({ ...admin, label: "Super Admin" });
      }
    });
  }

  if (currentChat.groupAdmin?._id) {
    allMembers.push({ ...currentChat.groupAdmin, label: "Admin" });
  }

  if (Array.isArray(currentChat.members)) {
    currentChat.members.forEach(member => {
      if (member?._id) {
        const isAdmin = allMembers.some(m => m._id === member._id);
        if (!isAdmin) {
          allMembers.push({ ...member, label: "Member" });
        }
      }
    });
  }

  const displayMembers = showAllMembers ? allMembers : allMembers.slice(0, 6);
  const hasMore = allMembers.length > 6;

  return (
    <div
      onClick={() => {
        onClose();
        setShowAllMembers(false);
      }}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1050,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '16px'
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#fff',
          borderRadius: '12px',
          maxWidth: '500px',
          width: '100%',
          maxHeight: '80vh',
          overflow: 'auto',
          boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
          scrollbarWidth: 'thin',
        }}
      >
        <div className="d-flex justify-content-between align-items-center p-3 border-bottom" style={{ backgroundColor: "#f5f5f5", position: "sticky", top: 0, zIndex: 1000 }}>
          <h5 className="mb-0">Group Details</h5>
          <Button
            color="link"
            className="p-0"
            onClick={() => {
              onClose();
              setShowAllMembers(false);
            }}
          >
            <i className="ri-close-line" style={{ fontSize: '24px' }}></i>
          </Button>
        </div>

        <div className="text-center p-4">
          {currentChat.groupImage ? (
            <img
              src={resolveMediaUrl(currentChat.groupImage)}
              alt={currentChat.name || "Group"}
              className="rounded-circle mb-3"
              style={{ width: 100, height: 100, objectFit: "cover" }}
              onError={(e) => {
                e.target.style.display = "none";
                const fallback = e.target.parentElement.querySelector('.dialog-avatar-fallback');
                if (fallback) fallback.style.display = "flex";
              }}
            />
          ) : <div
            className="rounded-circle bg-primary-subtle text-primary fw-bold d-flex align-items-center justify-content-center mx-auto mb-3 dialog-avatar-fallback"
            style={{
              width: 100,
              height: 100,
              display: currentChat.groupImage ? "none" : "flex",
              fontSize: "2rem"
            }}
          >
            {currentChat.name?.charAt(0)?.toUpperCase() || "G"}
          </div>}

          <h4 className="mb-0">{currentChat.name || "Group Chat"}</h4>
        </div>

        <div className="p-3">
          <h6 className="mb-3">All Members</h6>

          <div className="d-flex flex-column gap-3">
            {displayMembers.map((member, index) => {
              const hasInternalProfile =
                member.profileimg &&
                member.profileimg.trim() !== "" &&
                (member.profileimg.startsWith("/uploads") ||
                  member.profileimg.startsWith("uploads") ||
                  member.profileimg.startsWith("./uploads"));

              const isExternal =
                member.profileimg?.startsWith("http://") ||
                member.profileimg?.startsWith("https://");

              const shouldShowImage = hasInternalProfile && !isExternal;

              return (
                <div key={member._id || index} className="d-flex align-items-center gap-3">
                  {shouldShowImage ? (
                    <img
                      src={resolveMediaUrl(member.profileimg)}
                      alt={`${member.firstname || ""} ${member.lastname || ""}`.trim() || "Member"}
                      className="rounded-circle member-profile-img"
                      style={{
                        width: 50,
                        height: 50,
                        objectFit: "cover",
                      }}
                      onError={(e) => {
                        e.target.style.display = "none";
                        const fallback = e.target.parentElement.querySelector(".member-avatar-fallback");
                        if (fallback) fallback.style.display = "flex";
                      }}
                      onLoad={(e) => {
                        const fallback = e.target.parentElement.querySelector(".member-avatar-fallback");
                        if (fallback) fallback.style.display = "none";
                      }}
                    />
                  ) : null}

                  {(!shouldShowImage || isExternal) && (
                    <div
                      className="rounded-circle bg-primary-subtle text-primary fw-bold d-flex align-items-center justify-content-center member-avatar-fallback"
                      style={{
                        width: 50,
                        height: 50,
                        fontSize: "1.2rem",
                      }}
                    >
                      {(() => {
                        const first = member.firstname?.[0] || "";
                        const last = member.lastname?.[0] || "";
                        return (first + last || "M").toUpperCase();
                      })()}
                    </div>
                  )}

                  <div className="flex-grow-1">
                    <div className="d-flex align-items-center gap-2 justify-content-between">
                      <span className="fw-medium">
                        {`${member.firstname || ""} ${member.lastname || ""}`.trim() || "Member"}
                      </span>
                      <span
                        style={{ fontSize: "0.7rem", fontWeight: 600, color: "#b7b3b3" }}
                      >
                        {member.label}
                      </span>
                    </div>
                    {member.email && <small className="text-muted">{member.email}</small>}
                  </div>
                </div>
              );
            })}
          </div>

          {hasMore && !showAllMembers && (
            <div className="text-center mt-3">
              <Button
                color="link"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowAllMembers(true);
                }}
              >
                View More ({allMembers.length - 6} more)
              </Button>
            </div>
          )}

          {hasMore && showAllMembers && (
            <div className="text-center mt-3">
              <Button
                color="link"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowAllMembers(false);
                }}
              >
                View Less
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GroupDetailsDialog;

